/**
 * Game layer for Pick Your Mix: synthesized sound effects (WebAudio, no audio
 * files), candy levels driven by total ounces in the mix, and badges that stay
 * unlocked once earned (localStorage). The builder calls into this; nothing
 * here touches cart or state logic.
 */

const LEVELS = [
  { at: 0, name: 'Taster' },
  { at: 4, name: 'Snacker' },
  { at: 8, name: 'Candy Fan' },
  { at: 16, name: 'Candy Pro' },
  { at: 24, name: 'Candy Boss' },
  { at: 32, name: 'Candy Legend' },
];

const BADGES = [
  { id: 'first', mark: '1', color: '#F7B52C', name: 'First Scoop', desc: 'Add your first ounce.', test: (s) => s.totalOz >= 1 },
  { id: 'min', mark: '8', color: '#9ED36A', name: 'Minimum Met', desc: 'Reach the 8 oz order minimum.', test: (s) => s.cartTotal >= 8 },
  { id: 'rainbow', mark: '5', color: '#D0155C', name: 'Rainbow', desc: 'Mix 5 different flavors.', test: (s) => s.flavors >= 5 },
  { id: 'sour', mark: 'S', color: '#9ED36A', name: 'Sour Face', desc: '4 oz of sour candy.', test: (s) => (s.catOz.sour || 0) >= 4 },
  { id: 'sugar', mark: 'Y', color: '#F6C3D5', name: 'Sugar Rush', desc: '4 oz of sweet candy.', test: (s) => (s.catOz.sweet || 0) >= 4 },
  { id: 'explorer', mark: '3', color: '#5BC0EB', name: 'Explorer', desc: 'Pick from 3 different categories.', test: (s) => s.cats >= 3 },
  { id: 'full', mark: '15', color: '#F7B52C', name: 'Full Bag', desc: 'Fill a bag to the top.', test: (s) => s.fullBags >= 1 },
  { id: 'double', mark: '2x', color: '#D0155C', name: 'Double Bag', desc: 'Build two bags.', test: (s) => s.bags >= 2 },
  { id: 'collector', mark: '10', color: '#9B5DE5', name: 'Collector', desc: 'Mix 10 different flavors.', test: (s) => s.flavors >= 10 },
  { id: 'sealed', mark: '✓', color: '#1B0E19', name: 'Sealed', desc: 'Seal a mix into your cart.', test: (s, ctx) => !!ctx.sealed },
];

class MixSound {
  constructor() {
    this.ctx = null;
    this.master = null;
    try { this.enabled = localStorage.getItem('pym_sound') === 'on'; } catch (e) { this.enabled = false; } // sound is opt-in
  }

  unlock() {
    if (!this.enabled) return;
    try {
      if (!this.ctx) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        this.ctx = new AC();
        this.master = this.ctx.createGain();
        this.master.gain.value = 0.28;
        this.master.connect(this.ctx.destination);
      }
      if (this.ctx.state === 'suspended') this.ctx.resume();
    } catch (e) { /* audio unavailable */ }
  }

  toggle() {
    this.enabled = !this.enabled;
    try { localStorage.setItem('pym_sound', this.enabled ? 'on' : 'off'); } catch (e) { /* ignore */ }
    if (this.enabled) { this.unlock(); this.tap(); }
    return this.enabled;
  }

  _ok() { return this.enabled && this.ctx && this.master && this.ctx.state === 'running'; }

  _tone(freq, at, dur, opts = {}) {
    const { type = 'sine', gain = 0.18, to = null } = opts;
    const c = this.ctx, t0 = c.currentTime + at;
    const o = c.createOscillator(), g = c.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t0);
    if (to) o.frequency.exponentialRampToValueAtTime(to, t0 + dur);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    o.connect(g); g.connect(this.master);
    o.start(t0); o.stop(t0 + dur + 0.03);
  }

  _sweep(at, dur, f0, f1, gain) {
    const c = this.ctx, t0 = c.currentTime + at;
    const len = Math.floor(c.sampleRate * dur);
    const buf = c.createBuffer(1, len, c.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    const src = c.createBufferSource(); src.buffer = buf;
    const f = c.createBiquadFilter(); f.type = 'bandpass'; f.Q.value = 3;
    f.frequency.setValueAtTime(f0, t0); f.frequency.exponentialRampToValueAtTime(f1, t0 + dur);
    const g = c.createGain();
    g.gain.setValueAtTime(gain, t0); g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(f); f.connect(g); g.connect(this.master);
    src.start(t0);
  }

  tap() { if (!this._ok()) return; this._tone(620, 0, 0.07, { type: 'triangle', gain: 0.12, to: 900 }); }
  remove() { if (!this._ok()) return; this._tone(320, 0, 0.09, { type: 'triangle', gain: 0.12, to: 170 }); }
  land() {
    if (!this._ok()) return;
    const k = 0.9 + Math.random() * 0.25;
    this._tone(270 * k, 0, 0.1, { gain: 0.24, to: 130 * k });
    this._tone(1500 * k, 0, 0.03, { type: 'square', gain: 0.03 });
  }
  minimum() { if (!this._ok()) return; [523, 659, 784].forEach((f, i) => this._tone(f, i * 0.09, 0.22, { type: 'triangle', gain: 0.2 })); }
  level() { if (!this._ok()) return; [523, 659, 784, 1047].forEach((f, i) => this._tone(f, i * 0.08, 0.2, { type: 'triangle', gain: 0.2 })); }
  badge() { if (!this._ok()) return; this._tone(880, 0, 0.16, { gain: 0.2 }); this._tone(1319, 0.1, 0.3, { gain: 0.2 }); }
  full() { if (!this._ok()) return; [392, 523, 659, 784, 1047].forEach((f, i) => this._tone(f, i * 0.07, i === 4 ? 0.4 : 0.16, { type: 'triangle', gain: 0.2 })); }
  seal() {
    if (!this._ok()) return;
    this._sweep(0, 0.22, 500, 3000, 0.25);
    this._tone(1047, 0.24, 0.15, { gain: 0.22 });
    this._tone(1568, 0.32, 0.4, { gain: 0.22 });
  }
}

class MixGame {
  constructor(host) {
    this.host = host;
    this.sound = new MixSound();
    this.level = 0;
    try { this.unlocked = new Set(JSON.parse(localStorage.getItem('pym_badges') || '[]')); } catch (e) { this.unlocked = new Set(); }

    const wrap = host.querySelector('.scale__canvas-wrap');
    this.lvlChip = document.createElement('button');
    this.lvlChip.type = 'button';
    this.lvlChip.className = 'pymg__chip pymg__chip--lvl';
    this.lvlChip.setAttribute('aria-label', 'Open candy level and badges');
    this.badgeChip = document.createElement('button');
    this.badgeChip.type = 'button';
    this.badgeChip.className = 'pymg__chip pymg__chip--badges';
    this.badgeChip.setAttribute('aria-label', 'Open badges');
    this.soundBtn = document.createElement('button');
    this.soundBtn.type = 'button';
    this.soundBtn.className = 'pymg__snd';
    if (wrap) wrap.append(this.lvlChip, this.soundBtn); // the separate badge chip is gone; badges live in the level sheet

    this._buildSheet();
    host.addEventListener('pointerdown', () => this.sound.unlock());
    [this.lvlChip, this.badgeChip].forEach((b) => b.addEventListener('click', () => this.openSheet()));
    this.soundBtn.addEventListener('click', () => { this.sound.toggle(); this.render(); });

    this.check({ silent: true });
  }

  stats() {
    const st = this.host.state;
    const totals = {};
    let fullBags = 0, bags = 0;
    st.bags.forEach((b, i) => {
      const w = st.bagWeight(i);
      if (w > 0) bags++;
      if (w >= 15) fullBags++;
      Object.entries(b.items).forEach(([id, oz]) => { totals[id] = (totals[id] || 0) + oz; });
    });
    const catOz = {};
    let totalOz = 0, flavors = 0;
    Object.entries(totals).forEach(([id, oz]) => {
      if (oz <= 0) return;
      totalOz += oz; flavors++;
      const cat = st.candyById[id] && st.candyById[id].category;
      if (cat) catOz[cat] = (catOz[cat] || 0) + oz;
    });
    return { totalOz, flavors, catOz, cats: Object.keys(catOz).length, bags, fullBags, cartTotal: st.cartOzTotal + totalOz };
  }

  levelFor(oz) {
    let lv = 0;
    LEVELS.forEach((l, i) => { if (oz >= l.at) lv = i; });
    return lv;
  }

  // Compare against the last known level/badges; announce anything new.
  check(ctx = {}) {
    const s = this.stats();
    const lv = this.levelFor(s.totalOz);
    const newBadges = BADGES.filter((b) => !this.unlocked.has(b.id) && b.test(s, ctx));
    newBadges.forEach((b) => this.unlocked.add(b.id));
    if (newBadges.length) {
      try { localStorage.setItem('pym_badges', JSON.stringify([...this.unlocked])); } catch (e) { /* ignore */ }
    }
    // Levels and badges are tracked quietly: no pop-ups, sounds or confetti. They show in the level sheet.

    this.level = lv;
    this.render();
  }

  render() {
    const s = this.stats();
    const lv = this.levelFor(s.totalOz);
    this.lvlChip.textContent = LEVELS[lv].name;
    this.soundBtn.setAttribute('aria-label', this.sound.enabled ? 'Mute sounds' : 'Turn sounds on');
    this.soundBtn.setAttribute('aria-pressed', String(this.sound.enabled));
    this.soundBtn.innerHTML = this.sound.enabled
      ? '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M3 9v6h4l5 4V5L7 9H3z"/><path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" d="M15.5 8.5a5 5 0 010 7M18 6a8.5 8.5 0 010 12"/></svg>'
      : '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M3 9v6h4l5 4V5L7 9H3z"/><path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" d="M16 9l5 6M21 9l-5 6"/></svg>';
    if (this.sheet && !this.sheet.hidden) this._fillSheet();
  }

  _buildSheet() {
    const sheet = document.createElement('div');
    sheet.className = 'pymg__sheet';
    sheet.hidden = true;
    sheet.innerHTML = '<div class="pymg__backdrop" data-pymg-close></div>' +
      '<div class="pymg__panel" role="dialog" aria-modal="true" aria-label="Candy level and badges">' +
      '<button type="button" class="pymg__x" data-pymg-close aria-label="Close">&times;</button>' +
      '<div data-pymg-body></div></div>';
    this.sheet = sheet;
    this.host.appendChild(sheet);
    sheet.addEventListener('click', (e) => { if (e.target.closest('[data-pymg-close]')) this.closeSheet(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !sheet.hidden) this.closeSheet(); });
  }

  openSheet() {
    this._fillSheet();
    this.sheet.hidden = false;
    document.documentElement.classList.add('pymg-lock');
    const x = this.sheet.querySelector('.pymg__x');
    if (x) x.focus();
    this.sound.tap();
  }

  closeSheet() {
    this.sheet.hidden = true;
    document.documentElement.classList.remove('pymg-lock');
    this.lvlChip.focus();
  }

  _fillSheet() {
    const s = this.stats();
    const lv = this.levelFor(s.totalOz);
    const next = LEVELS[lv + 1];
    const base = LEVELS[lv].at;
    const pct = next ? Math.round(((s.totalOz - base) / (next.at - base)) * 100) : 100;
    const badges = BADGES.map((b) => {
      const on = this.unlocked.has(b.id);
      return '<li class="pymg__badge' + (on ? ' is-on' : '') + '">' +
        '<span class="pymg__medal" style="--c:' + b.color + '">' + (on ? b.mark : '?') + '</span>' +
        '<span class="pymg__btxt"><b>' + b.name + '</b><span>' + (on ? b.desc : 'Locked: ' + b.desc) + '</span></span></li>';
    }).join('');
    this.sheet.querySelector('[data-pymg-body]').innerHTML =
      '<p class="pymg__kick">Your candy level</p>' +
      '<h3 class="pymg__lv">LVL ' + (lv + 1) + ' &middot; ' + LEVELS[lv].name + '</h3>' +
      '<div class="pymg__xp"><span style="width:' + pct + '%"></span></div>' +
      '<p class="pymg__next">' + (next ? (next.at - s.totalOz) + ' oz to ' + next.name : 'Top level reached') + '</p>' +
      '<h4 class="pymg__bh">Badges ' + this.unlocked.size + '/' + BADGES.length + '</h4>' +
      '<ul class="pymg__badges">' + badges + '</ul>';
  }
}

window.MixGame = MixGame;
